import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import type { ProductUpdatesEntry } from './types';
import styled from 'styled-components';
import {
  Box, Button, Text,
} from '@usealloy/component-library';
import type { UseProductUpdatesDataResult } from './useProductUpdatesData';
import { ContentReset } from './ProductUpdates.styles';

import { PRODUCT_UPDATES_URL } from '~/constants';

export interface ProductUpdatesProps {
  productUpdatesData: UseProductUpdatesDataResult;
}


interface ProductUpdatesEntryComponentProps {
  entry: ProductUpdatesEntry;
}

const ProductUpdatesEntryDivider = styled.hr`
  /* Reset horizontal rules */
  margin: 1rem 0;
  width: 100%;
  padding: 0;
  border: none;
  height: 1px;
  background: inherit;
  border-top: 1px solid var(--cl-gray-300);
`;

const ProductUpdatesEntryComponent = ({
  entry,
}: ProductUpdatesEntryComponentProps) => (
  <Box direction="column" alignItems="start" gap={3} key={entry.id}>

    <Box direction="column" alignItems="start" gap={2}>
      <Text size={3} weight={700}>{entry.title}</Text>
      <Text size={1}>{dayjs(entry.postDate).format('MMMM DD, YYYY')}</Text>
    </Box>

    <ContentReset
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(entry.richText || ''),
      }}
    />
  </Box>
);


/**
 * The ProductUpdates component is used to display a list of product update entries.
 *
 * It receives the result object from useProductUpdatesData hook and formats and displays the data for use inside our application.
 *
 * It's designed to be used inside of a modal, but can be used anywhere.
 *
 * The consumer is responsible for fetching data using the useProductUpdatesData hook.
 */
export const ProductUpdates = ({ productUpdatesData }: ProductUpdatesProps) => {
  const {
    data, loading, error, getData,
  } = productUpdatesData;

  if (loading) {
    return (
      <div>
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <Box
        direction="column"
        gap={4}
      >
        <Text>
          We couldn&apos;t load the Product Updates right now. Go to the{' '}
          <a href={PRODUCT_UPDATES_URL} target="_blank" rel="noreferrer">
            Alloy Content Library
          </a>{' '}
          for updates.
        </Text>
        {getData && <Button onClick={getData}>Retry</Button>}
      </Box>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div>
        <Text>No product update entries found.</Text>
      </div>
    );
  }

  // Helper function to check if an entry is valid and not empty
  const isValidEntry = (entry: ProductUpdatesEntry): boolean => {
    if (!entry || typeof entry !== 'object') return false;
    return !!(
      entry.id
      && entry.title
      && entry.uri
      && Array.isArray(entry.productUpdateTags)
      && Array.isArray(entry.productUpdateCategory)
    );
  };

  // Filter out invalid entries
  const validEntries = data.filter(isValidEntry);

  // Show empty state if all entries were not valid
  if (validEntries.length === 0) {
    return (
      <div>
        <Text>No product update entries found.</Text>
      </div>
    );
  }

  return (
    <div>
      {validEntries?.map(entry => (
        <>
          <ProductUpdatesEntryComponent key={entry.id} entry={entry} />
          <ProductUpdatesEntryDivider />
        </>
      ))}
    </div>
  );
};
